import { useEffect, useLayoutEffect, useRef, useState } from "react";

interface PageContextMenuProps {
  x: number;
  y: number;
  canDelete: boolean;
  onRename: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onClose: () => void;
}

export function PageContextMenu({
  x,
  y,
  canDelete,
  onRename,
  onDuplicate,
  onDelete,
  onClose,
}: PageContextMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState(() => ({ left: x, top: y }));

  useLayoutEffect(() => {
    function updatePosition(): void {
      const menu = menuRef.current;
      const margin = 8;
      const width = menu?.offsetWidth ?? 160;
      const height = menu?.offsetHeight ?? 120;
      setPosition({
        left: Math.min(Math.max(margin, x), Math.max(margin, window.innerWidth - width - margin)),
        top: Math.min(Math.max(margin, y), Math.max(margin, window.innerHeight - height - margin)),
      });
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, [x, y]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <>
      <button
        type="button"
        aria-label="Close context menu"
        className="fixed inset-0 z-40 cursor-default"
        onClick={onClose}
        onContextMenu={event => {
          event.preventDefault();
          onClose();
        }}
      />
      <div
        ref={menuRef}
        className="fixed z-50 overflow-y-auto rounded-md border border-white/10 bg-[#242a31] p-1 text-[12px] text-slate-200 shadow-xl shadow-black/30"
        style={{ left: position.left, top: position.top, width: "min(10rem, calc(100vw - 16px))" }}
        role="menu"
      >
        <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={onRename} role="menuitem">
          Rename
        </button>
        <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={onDuplicate} role="menuitem">
          Duplicate
        </button>
        <button
          type="button"
          className={`h-8 w-full rounded px-2 text-left ${canDelete ? "hover:bg-white/[0.08]" : "cursor-not-allowed text-slate-500"}`}
          onClick={canDelete ? onDelete : undefined}
          disabled={!canDelete}
          role="menuitem"
        >
          Delete
        </button>
      </div>
    </>
  );
}
